import {
  ExchangeOffice,
  ExchangeOfficeRequest,
  Currency,
  CurrencyRequest,
  AdminLoginCredentials,
  OfficeLoginCredentials,
  LoginResponse,
  ApiResponse,
} from '../types';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';
const ADMIN_TOKEN_KEY = process.env.REACT_APP_ADMIN_TOKEN_KEY || 'admin-token';
const OFFICE_TOKEN_KEY = process.env.REACT_APP_OFFICE_TOKEN_KEY || 'office-token';
export const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || 'http://localhost:8000/storage';

// Token Management
export const getAdminToken = (): string | null => localStorage.getItem(ADMIN_TOKEN_KEY);
export const setAdminToken = (token: string): void => localStorage.setItem(ADMIN_TOKEN_KEY, token);
export const removeAdminToken = (): void => localStorage.removeItem(ADMIN_TOKEN_KEY);

export const getOfficeToken = (): string | null => localStorage.getItem(OFFICE_TOKEN_KEY);
export const setOfficeToken = (token: string): void => localStorage.setItem(OFFICE_TOKEN_KEY, token);
export const removeOfficeToken = (): void => localStorage.removeItem(OFFICE_TOKEN_KEY);

// Helper function for API requests
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  useAdminToken: boolean = true
): Promise<T> {
  const token = useAdminToken ? getAdminToken() : getOfficeToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    // Even if no json, try to get error message, fallback to status
    let errorMessage = `API Error: ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (_) {
      // ignore if not json
    }
    throw new Error(errorMessage);
  }

  // Try parse json. But sometimes there is no body!
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.indexOf('application/json') !== -1) {
    return response.json();
  } else {
    // @ts-ignore
    return undefined; // or you can throw, or return null based on your app need
  }
}

// ============================================
// AUTHENTICATION APIs
// ============================================

// Admin Login
export async function adminLogin(credentials: AdminLoginCredentials): Promise<LoginResponse> {
  const response = await apiRequest<LoginResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({
      identifier: credentials.identifier,
      password: credentials.password,
    }),
  });

  if (response.token) {
    setAdminToken(response.token);
  }

  return response;
}

// Office Login
export async function officeLogin(credentials: OfficeLoginCredentials): Promise<LoginResponse> {
  const response = await apiRequest<LoginResponse>('/exchange-offices/auth/login', {
    method: 'POST',
    body: JSON.stringify({
      username: credentials.username,
      password: credentials.password,
    }),
  });

  if (response.token) {
    console.log(response);
    setOfficeToken(response.token);
  }

  return response;
}

// Admin Logout
export async function adminLogout(): Promise<void> {
  try {
    await apiRequest('/auth/logout', { method: 'POST' });
  } finally {
    removeAdminToken();
  }
}

// Office Logout
export async function officeLogout(): Promise<void> {
  try {
    await apiRequest('/exchange-offices/auth/logout', { method: 'POST' }, false);
  } finally {
    removeOfficeToken();
  }
}

// ============================================
// EXCHANGE OFFICE APIs (Admin)
// ============================================

// Get all offices
export async function getOffices(): Promise<ExchangeOffice[]> {
  const response = await apiRequest<ApiResponse<ExchangeOffice[]>>('/exchange-offices');
  return response.data || [];
}

// Get single office
export async function getOffice(id: number): Promise<ExchangeOffice> {
  const response = await apiRequest<any>(`/exchange-offices/${id}`);
  return response;
}

// Create office
export async function createOffice(office: ExchangeOfficeRequest): Promise<ExchangeOffice> {
  const requestBody: Record<string, any> = {
    name: office.name,
    website: office.website || null,
    account_username: office.accountUsername || office.account_username,
    accountUsername: office.accountUsername || office.account_username,
    account_password: office.accountPassword || office.account_password,
    accountPassword: office.accountPassword || office.account_password,
    // Media IDs
    logo_id: office.logoId || office.logo_id || null,
    logoId: office.logoId || office.logo_id || null,
    video_id: office.videoId || office.video_id || null,
    videoId: office.videoId || office.video_id || null,
    video_option_id: office.videoOptionId || office.video_option_id || null,
    videoOptionId: office.videoOptionId || office.video_option_id || null,
    status: office.status || 'active',
  };

  const response = await apiRequest<ApiResponse<ExchangeOffice>>('/exchange-offices', {
    method: 'POST',
    body: JSON.stringify(requestBody),
  });
  return response.data;
}

// Update office
export async function updateOffice(id: number, office: Partial<ExchangeOfficeRequest>): Promise<ExchangeOffice> {
  const requestBody: Record<string, any> = {};

  // Only include fields that are provided
  if (office.name !== undefined) requestBody.name = office.name;
  if (office.website !== undefined) requestBody.website = office.website;
  if (office.accountUsername || office.account_username) {
    requestBody.account_username = office.accountUsername || office.account_username;
    requestBody.accountUsername = office.accountUsername || office.account_username;
  }
  if (office.accountPassword || office.account_password) {
    requestBody.account_password = office.accountPassword || office.account_password;
    requestBody.accountPassword = office.accountPassword || office.account_password;
  }
  if (office.status !== undefined) requestBody.status = office.status;

  // Media IDs - include even if null (to clear them)
  if (office.logoId !== undefined || office.logo_id !== undefined) {
    requestBody.logo_id = office.logoId ?? office.logo_id ?? null;
    requestBody.logoId = office.logoId ?? office.logo_id ?? null;
  }
  if (office.videoId !== undefined || office.video_id !== undefined) {
    requestBody.video_id = office.videoId ?? office.video_id ?? null;
    requestBody.videoId = office.videoId ?? office.video_id ?? null;
  }
  if (office.videoOptionId !== undefined || office.video_option_id !== undefined) {
    requestBody.video_option_id = office.videoOptionId ?? office.video_option_id ?? null;
    requestBody.videoOptionId = office.videoOptionId ?? office.video_option_id ?? null;
  }

  const response = await apiRequest<ApiResponse<ExchangeOffice>>(`/exchange-offices/${id}`, {
    method: 'PUT',
    body: JSON.stringify(requestBody),
  });
  return response.data;
}

// Delete office
export async function deleteOffice(id: number): Promise<void> {
  await apiRequest(`/exchange-offices/${id}`, {
    method: 'DELETE',
  });
}

// ============================================
// CURRENCY APIs (Admin – apiResource /currencies)
// ============================================

/** Build request body for currency create/update (exchange_office_id, name_en/nameAr, etc.) */
function currencyToBody(
  c: CurrencyRequest | Partial<CurrencyRequest> & { iso?: string; buy?: number; sell?: number },
  officeId: number
) {
  const nameEn = c.nameEn ?? (c as CurrencyRequest).name_en ?? null;
  const nameAr = c.nameAr ?? (c as CurrencyRequest).name_ar ?? null;
  return {
    exchange_office_id: officeId,
    exchangeOfficeId: officeId,
    flag: c.flag ?? null,
    name_en: nameEn,
    nameEn,
    name_ar: nameAr,
    nameAr,
    iso: c.iso,
    units: c.units ?? null,
    buy: c.buy,
    sell: c.sell,
    order: c.order ?? 0,
  };
}

/** Admin: create currency — POST /currencies */
export async function createCurrencyForOffice(
  officeId: number,
  currency: CurrencyRequest
): Promise<Currency> {
  const res = await apiRequest<ApiResponse<Currency>>('/currencies', {
    method: 'POST',
    body: JSON.stringify(currencyToBody(currency, officeId)),
  });
  return res.data;
}

/** Admin: bulk create — multiple POST /currencies (apiResource has no bulk) */
export async function createCurrenciesForOffice(
  officeId: number,
  currencies: CurrencyRequest[]
): Promise<Currency[]> {
  const out: Currency[] = [];
  for (let i = 0; i < currencies.length; i++) {
    const body = currencyToBody({ ...currencies[i], order: currencies[i].order ?? i }, officeId);
    const res = await apiRequest<ApiResponse<Currency>>('/currencies', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    out.push(res.data);
  }
  return out;
}

/** Admin: update currency — PUT /currencies/:id. Pass full currency data. */
export async function updateCurrencyForOffice(
  officeId: number,
  currencyId: number,
  data: CurrencyRequest
): Promise<Currency> {
  const res = await apiRequest<ApiResponse<Currency>>(`/currencies/${currencyId}`, {
    method: 'PUT',
    body: JSON.stringify(currencyToBody(data, officeId)),
  });
  return res.data;
}

/** Admin: delete currency — DELETE /currencies/:id */
export async function deleteCurrencyForOffice(
  _officeId: number,
  currencyId: number
): Promise<void> {
  await apiRequest(`/currencies/${currencyId}`, { method: 'DELETE' });
}

// ============================================
// CURRENCY APIs (Office)
// ============================================

// Get current office details (for logged in office)
export async function getCurrentOffice(): Promise<ExchangeOffice> {
  const response = await apiRequest<ApiResponse<ExchangeOffice> | ExchangeOffice>('/exchange-offices/auth/me', {}, false);
  // Handle both wrapped and unwrapped response
  if ('data' in response && response.data) {
    return response.data as ExchangeOffice;
  }
  return response as ExchangeOffice;
}

// Get office currencies
export async function getOfficeCurrencies(): Promise<Currency[]> {
  const response = await apiRequest<ApiResponse<Currency[]> | Currency[]>('/exchange-offices/auth/currencies', {}, false);
  // Handle both wrapped and unwrapped response
  if (response && 'data' in response) {
    return (response as ApiResponse<Currency[]>).data || [];
  }
  return (response as Currency[]) || [];
}

// Create single currency
export async function createCurrency(currency: CurrencyRequest): Promise<Currency> {
  const response = await apiRequest<ApiResponse<Currency>>('/exchange-offices/auth/currencies', {
    method: 'POST',
    body: JSON.stringify({
      exchange_office_id: currency.exchangeOfficeId || currency.exchange_office_id,
      exchangeOfficeId: currency.exchangeOfficeId || currency.exchange_office_id,
      flag: currency.flag || null,
      name_en: currency.nameEn || currency.name_en,
      nameEn: currency.nameEn || currency.name_en,
      name_ar: currency.nameAr || currency.name_ar,
      nameAr: currency.nameAr || currency.name_ar,
      iso: currency.iso,
      units: currency.units || null,
      buy: currency.buy,
      sell: currency.sell,
      order: currency.order || 0,
    }),
  }, false);
  return response.data;
}

// Create multiple currencies
export async function createCurrencies(currencies: CurrencyRequest[]): Promise<Currency[]> {
  const response = await apiRequest<ApiResponse<Currency[]>>('/exchange-offices/auth/currencies/bulk', {
    method: 'POST',
    body: JSON.stringify({
      currencies: currencies.map(currency => ({
        exchange_office_id: currency.exchangeOfficeId || currency.exchange_office_id,
        exchangeOfficeId: currency.exchangeOfficeId || currency.exchange_office_id,
        flag: currency.flag || null,
        name_en: currency.nameEn || currency.name_en,
        nameEn: currency.nameEn || currency.name_en,
        name_ar: currency.nameAr || currency.name_ar,
        nameAr: currency.nameAr || currency.name_ar,
        iso: currency.iso,
        units: currency.units || null,
        buy: currency.buy,
        sell: currency.sell,
        order: currency.order || 0,
      })),
    }),
  }, false);
  return response.data || [];
}

// Update currency
export async function updateCurrency(id: number, currency: Partial<CurrencyRequest>): Promise<Currency> {
  const response = await apiRequest<ApiResponse<Currency>>(`/exchange-offices/auth/currencies/${id}`, {
    method: 'PUT',
    body: JSON.stringify({
      flag: currency.flag,
      name_en: currency.nameEn || currency.name_en,
      nameEn: currency.nameEn || currency.name_en,
      name_ar: currency.nameAr || currency.name_ar,
      nameAr: currency.nameAr || currency.name_ar,
      iso: currency.iso,
      units: currency.units,
      buy: currency.buy,
      sell: currency.sell,
      order: currency.order,
    }),
  }, false);
  return response.data;
}

// Delete currency
export async function deleteCurrency(id: number): Promise<void> {
  await apiRequest(`/exchange-offices/auth/currencies/${id}`, {
    method: 'DELETE',
  }, false);
}

// Update office display settings (from office login) - DEPRECATED, use updateCurrentOffice
export async function updateOfficeSettings(settings: {
  logo?: string;
  video?: string;
  videoOption?: string;
}): Promise<ExchangeOffice> {
  const response = await apiRequest<ApiResponse<ExchangeOffice>>('/office/settings', {
    method: 'PUT',
    body: JSON.stringify({
      logo: settings.logo,
      video: settings.video,
      video_option: settings.videoOption,
      videoOption: settings.videoOption,
    }),
  }, false);
  return response.data;
}

// Update current office (for logged-in office user) using media IDs
export async function updateCurrentOffice(data: {
  name?: string;
  website?: string | null;
  logoId?: number | null;
  logo_id?: number | null;
  videoId?: number | null;
  video_id?: number | null;
  videoOptionId?: number | null;
  video_option_id?: number | null;
  status?: 'active' | 'inactive';
}): Promise<ExchangeOffice> {
  const requestBody: Record<string, any> = {};

  const officeStr = localStorage.getItem('office-user');

  const office = officeStr ? JSON.parse(officeStr) : null;
  // Only include fields that are provided
  if (data.name !== undefined) requestBody.name = data.name;
  if (data.website !== undefined) requestBody.website = data.website;
  if (data.status !== undefined) requestBody.status = data.status;

  // Media IDs - include even if null (to clear them)
  if (data.logoId !== undefined || data.logo_id !== undefined) {
    requestBody.logo_id = data.logoId ?? data.logo_id ?? null;
    requestBody.logoId = data.logoId ?? data.logo_id ?? null;
  }
  if (data.videoId !== undefined || data.video_id !== undefined) {
    requestBody.video_id = data.videoId ?? data.video_id ?? null;
    requestBody.videoId = data.videoId ?? data.video_id ?? null;
  }
  if (data.videoOptionId !== undefined || data.video_option_id !== undefined) {
    requestBody.video_option_id = data.videoOptionId ?? data.video_option_id ?? null;
    requestBody.videoOptionId = data.videoOptionId ?? data.video_option_id ?? null;
  }

  const response = await apiRequest<any>(`/exchange-offices/${office?.id}`, {
    method: 'PUT',
    body: JSON.stringify(requestBody),
  }, false);
  return response;
}

// ============================================
// MEDIA UPLOAD APIs
// ============================================

// Medium types enum (matching Laravel MediumType)
export enum MediumType {
  IMAGE = 1,
  VIDEO = 1,
  AUDIO = 1,
}

// Medium for enum (matching Laravel MediumFor)
export enum MediumFor {
  LOGO = 'user-profile',
  VIDEO = 'user-profile',
  GENERAL = 'user-profile',
}

export interface MediaUploadResponse {
  id: number;
  url: string;
  path: string;
  filename: string;
  mimeType: string;
  size: number;
}

// Allowed file extensions
export const ALLOWED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
export const ALLOWED_VIDEO_EXTENSIONS = ['mp4', 'avi', 'mov', 'wmv', 'webm', 'mkv', 'mpeg', 'mpg', 'flv'];
export const ALLOWED_AUDIO_EXTENSIONS = ['mp3', 'wav', 'aac', 'm4a', 'ogg', 'flac', 'wma'];
export const ALL_ALLOWED_EXTENSIONS = [...ALLOWED_IMAGE_EXTENSIONS, ...ALLOWED_VIDEO_EXTENSIONS, ...ALLOWED_AUDIO_EXTENSIONS];

// Max file size: 25MB
export const MAX_FILE_SIZE = 25 * 1024 * 1024;

// Helper to get medium type from file
export function getMediumTypeFromFile(file: File): MediumType {
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  if (ALLOWED_IMAGE_EXTENSIONS.includes(ext)) return MediumType.IMAGE;
  if (ALLOWED_VIDEO_EXTENSIONS.includes(ext)) return MediumType.VIDEO;
  if (ALLOWED_AUDIO_EXTENSIONS.includes(ext)) return MediumType.AUDIO;
  return MediumType.IMAGE; // default
}

// Helper to validate file (skipSizeCheck: true = لا حد للحجم، يُستخدم لرفع الفيديو)
export function validateMediaFile(
  file: File,
  options?: { skipSizeCheck?: boolean }
): { valid: boolean; error?: string } {
  const ext = file.name.split('.').pop()?.toLowerCase() || '';

  if (!ALL_ALLOWED_EXTENSIONS.includes(ext)) {
    return { valid: false, error: `نوع الملف غير مدعوم. الأنواع المسموحة: ${ALL_ALLOWED_EXTENSIONS.join(', ')}` };
  }

  // if (!options?.skipSizeCheck && file.size > MAX_FILE_SIZE) {
  //   return { valid: false, error: 'حجم الملف يتجاوز الحد المسموح (25 ميجابايت)' };
  // }

  return { valid: true };
}

/**
 * Upload media file
 * API expects: medium (file), type (int), for (string)
 */
export async function uploadMedia(
  file: File,
  mediumFor: MediumFor,
  useAdminToken: boolean = true
): Promise<MediaUploadResponse> {
  const token = useAdminToken ? getAdminToken() : getOfficeToken();

  const formData = new FormData();
  formData.append('medium', file);
  formData.append('type', String(getMediumTypeFromFile(file)));
  formData.append('for', mediumFor);

  const response = await fetch(`${API_BASE_URL}/media`, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `فشل الرفع: ${response.status}`);
  }

  const data = await response.json();
  const mediaData = data.data || data;
  return mediaData;
}

// Upload media file - Office (convenience wrapper)
export async function uploadOfficeMedia(file: File, mediumFor: MediumFor): Promise<MediaUploadResponse> {
  return uploadMedia(file, mediumFor, false);
}

// Get media URL helper
export function getMediaUrl(path: string): string {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  return `${MEDIA_URL}/${path}`;
}

// ============================================
// PUBLIC APIs
// ============================================

// Get public currencies for an exchange office
// Returns array of currencies with embedded exchangeOffice object
export async function getPublicCurrencies(exchangeOfficeId: number): Promise<{data: Currency[], lastUpdatedAt: string}> {
  const response = await fetch(`${API_BASE_URL}/public/exchange-offices/${exchangeOfficeId}/currencies`, {
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch currencies: ${response.status}`);
  }

  const data = await response.json();
  return {data:  data.data  , lastUpdatedAt :data?.lastUpdatedAt}  ;
}

// Get public office details
export async function getPublicOffice(exchangeOfficeId: number): Promise<ExchangeOffice> {
  const response = await fetch(`${API_BASE_URL}/public/exchange-offices/${exchangeOfficeId}`, {
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch office: ${response.status}`);
  }

  const data = await response.json();
  return data.data || data;
}

// Main domain for subdomain detection (e.g. screen-rates.com)
const MAIN_DOMAIN = process.env.REACT_APP_MAIN_DOMAIN || 'screen-rates.com';

/**
 * Get subdomain from hostname when on MAIN_DOMAIN.
 * e.g. "office.screen-rates.com" -> "office"; "www.screen-rates.com" / "screen-rates.com" -> null
 */
export function getSubdomainSlug(): string | null {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname.toLowerCase();
  const base = MAIN_DOMAIN.toLowerCase();
  if (!host.endsWith(base) || host === base) return null;
  const prefix = host.slice(0, -(base.length + 1));
  if (!prefix || prefix === 'www') return null;
  return prefix;
}

/**
 * Fetch public currencies by office slug/name (e.g. from subdomain or path).
 * Use when identifier is not numeric. Backend: GET /public/exchange-offices/by-slug/{slug}/currencies
 */
export async function getPublicCurrenciesBySlug(slug: string): Promise<{data: Currency[], lastUpdatedAt: string}> {
  const encoded = encodeURIComponent(slug);
  const response = await fetch(
    `${API_BASE_URL}/public/exchange-offices/by-slug/${encoded}/currencies`,
    { headers: { Accept: 'application/json' } }
  );

  if (!response.ok) {
    if (response.status === 404) throw new Error('Office not found');
    throw new Error(`Failed to fetch currencies: ${response.status}`);
  }

  const data = await response.json();
  return {data:  data.data || [], lastUpdatedAt :data?.lastUpdatedAt} as {data: Currency[], lastUpdatedAt: string};
}
